import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import addToCart from "../cart/addToCart";
import { useAuth } from "../../contexts/AuthContext";
import "swiper/css";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

interface Product {
  id: string;
  name: string;
  price: number;
  description: string;
  image: string;
  rating: string;
  reviews: string;
  ram: string;
  rom: string;
  color: string;
  os: string;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      try {
        const db = getFirestore();
        const productRef = doc(db, "proudcts", id);
        const productSnap = await getDoc(productRef);

        if (productSnap.exists()) {
          const productData = {
            ...productSnap.data(),
            id: productSnap.id,
          } as Product;
          setProduct(productData);

          const relatedQuery = query(
            collection(db, "proudcts"),
            where("os", "==", productData.os),
            where("__name__", "!=", id)
          );
          const relatedSnap = await getDocs(relatedQuery);
          const relatedItems = relatedSnap.docs.map((doc) => {
            const data = doc.data();
            return { ...data, id: doc.id } as Product;
          });

          setRelatedProducts(relatedItems);
        } else {
          console.error("Продукт не найден");
        }
      } catch (error) {
        console.error("Ошибка при запросе данных:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    if (!id) {
      alert("Ошибка: ID продукта не определен.");
      return;
    }
    if (!currentUser) {
      alert("Please log in to add items to your cart.");
      return;
    }

    try {
      await addToCart(currentUser.uid, id);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  if (loading) return <div className="text-center py-8">Загрузка...</div>;
  if (!product)
    return <div className="text-center py-8">Продукт не найден</div>;

  return (
    <div className="mx-auto my-10 px-5 lg:px-36">
      <div className="flex flex-col items-center lg:flex-row gap-6">
        <div className="flex-1">
          <img
            src={product.image}
            alt={product.name}
            className="w-full rounded-lg shadow-xl"
          />
        </div>

        <div className="flex-1 space-y-6">
          <h3 className="text-3xl font-semibold text-gray-800">
            {product.name}
          </h3>
          <p className="text-xl text-gray-600">{product.description}</p>

          <div className="flex items-center gap-4">
            <p className="text-2xl font-bold text-green-600">
              {product.price} USD
            </p>
            <div className="flex items-center gap-1">
              <span className="text-yellow-400">★★★★★</span>
              <span className="text-gray-500">({product.reviews} reviews)</span>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-800">
              Characteristics:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-medium text-gray-700">RAM</h4>
                <p className="text-gray-500">{product.ram} GB</p>
              </div>
              <div className="border p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-medium text-gray-700">ROM</h4>
                <p className="text-gray-500">{product.rom} GB</p>
              </div>
              <div className="border p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-medium text-gray-700">Color</h4>
                <p className="text-gray-500">{product.color}</p>
              </div>
            </div>
          </div>

          <div>
            <button
              onClick={handleAddToCart}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add To Cart
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
          NOT SURE? BROWSE SIMILAR PRODUCTS
        </h2>
        <Swiper spaceBetween={20} slidesPerView={3}>
          {relatedProducts.map((relatedProduct) => (
            <SwiperSlide key={relatedProduct.id}>
              <div className="border rounded-lg shadow-sm p-4">
                <Link to={`/products/${relatedProduct.id}`}>
                  <img
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                    className="w-full h-40 object-contain rounded-lg mb-4"
                  />
                </Link>
                <Link
                  to={`/products/${relatedProduct.id}`}
                  className="text-lg font-semibold text-gray-800"
                >
                  {relatedProduct.name}
                </Link>
                <p className="text-sm text-gray-500">
                  {relatedProduct.price} USD
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default ProductDetail;
